import { useKeepAwake } from "@-/com.npmjs.expo-keep-awake";
import { onVolumeChange } from "@-/dev.improve.haniverse.modules.audio-input";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useState, useSyncExternalStore, type PropsWithChildren } from "react";
import { createVoiceStudyAudio } from "#create-voice-study-audio";
import { VoiceStudyContext } from "#voice-study-context";
import { VoiceStudyEnabledIfProvidedContext } from "#voice-study-enabled-if-provided-context";
import { VoiceStudyController, type VoiceStudyTurn } from "#voice-study-controller";
import { createVoiceStudyLockScreen } from "#voice-study-lock-screen";
import {
  useVoiceStudySessionLocked,
  useVoiceStudySessionPaused,
  type VoiceStudySessionHandle,
} from "#voice-study-session-handle";

/**
 * Voice study for one Learn stack. `focused` is the stack's focus in the tab navigator (leaving
 * it ends the session; locking the phone does not change it); `muted` is the app-wide mute.
 */
export function VoiceStudyProvider({
  children,
  focused,
  muted,
  setMuted,
}: PropsWithChildren<{ focused: boolean; muted: boolean; setMuted: (muted: boolean) => void }>) {
  const muting = useLatest({ muted, setMuted });
  const [controller] = useState(
    () => new VoiceStudyController(createVoiceStudyAudio(), createVoiceStudyLockScreen(), muting),
  );
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const { lifecycle } = snapshot;

  // Leaving the Learn stack ends the session; locking the phone does not (the tab stays focused).
  useEffect(() => {
    if (!focused && (lifecycle.kind === "active" || lifecycle.kind === "starting")) {
      controller.stop();
    }
  }, [controller, focused, lifecycle.kind]);

  useEffect(() => controller.subscribeRemote(), [controller]);

  useEffect(() => () => controller.dispose(), [controller]);

  return (
    <VoiceStudyContext.Provider value={snapshot}>
      <VoiceStudyEnabledIfProvidedContext value={lifecycle.kind === "active"}>
        {lifecycle.kind === "active" && (
          <VoiceStudyActiveSessionEffects
            controller={controller}
            focused={focused}
            muted={muted}
            session={lifecycle.session}
            turn={snapshot.turn}
          />
        )}
        {children}
      </VoiceStudyEnabledIfProvidedContext>
    </VoiceStudyContext.Provider>
  );
}

/** Mounted exactly while a session is active: runs the focused card's turn and keeps the screen on. */
function VoiceStudyActiveSessionEffects({
  controller,
  focused,
  muted,
  session,
  turn,
}: {
  controller: VoiceStudyController;
  focused: boolean;
  muted: boolean;
  session: VoiceStudySessionHandle;
  turn: VoiceStudyTurn | null;
}) {
  const paused = useVoiceStudySessionPaused(session);
  const locked = useVoiceStudySessionLocked(session);

  useEffect(() => {
    if (paused) {
      controller.clearSessionTurn();
      return;
    }
    controller.runTurn(turn, muted);
  }, [controller, muted, paused, turn]);

  // Any hardware volume change releases the touch lock.
  useEffect(() => {
    if (!locked) {
      return;
    }
    const subscription = onVolumeChange(() => session.setLocked(false));
    return () => subscription.remove();
  }, [locked, session]);

  return <>{!paused && focused && <VoiceStudyKeepAwake />}</>;
}

function VoiceStudyKeepAwake() {
  useKeepAwake("han-voice-study", { suppressDeactivateWarnings: true });
  return null;
}
